
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { supabase } from "@/integrations/supabase/client";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Post } from '@/types/board';

const NewsDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [newsItem, setNewsItem] = useState<Post | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchNewsItem = async () => {
      if (!id) return;
      
      try {
        const { data, error } = await supabase
          .from('board_posts')
          .select('*')
          .eq('id', id)
          .eq('category', '로컬뉴스')
          .single();
        
        if (error) {
          console.error('Error fetching news item:', error);
          toast({
            variant: "destructive",
            title: "뉴스를 불러오는데 실패했습니다",
            description: "잠시 후 다시 시도해주세요."
          });
          navigate('/news');
          return;
        }
        
        setNewsItem(data as Post);
      } catch (error) {
        console.error('Failed to fetch news item:', error);
        toast({
          variant: "destructive",
          title: "뉴스를 불러오는데 실패했습니다",
          description: "잠시 후 다시 시도해주세요."
        });
        navigate('/news');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchNewsItem();
    
    // Increment view count regardless of authentication
    const incrementViewCount = async () => {
      if (!id) return;
      
      try {
        const { data } = await supabase
          .from('board_posts')
          .select('view_count')
          .eq('id', id)
          .single();
          
        if (data) {
          // Generate random increment between 1 and 6
          const randomIncrement = Math.floor(Math.random() * 6) + 1;
          
          await supabase
            .from('board_posts')
            .update({ view_count: (data.view_count || 0) + randomIncrement })
            .eq('id', id);
        }
      } catch (error) {
        console.error('Error incrementing view count:', error);
      }
    };
    
    incrementViewCount();
  }, [id, navigate, toast]);

  const formatDate = (date: string) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');
    
    return `${year}.${month}.${day} ${hour}:${minute}`;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto py-8 px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-muted rounded w-32 mb-8"></div>
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded"></div>
              <div className="h-4 bg-muted rounded"></div>
              <div className="h-4 bg-muted rounded"></div>
              <div className="h-4 bg-muted rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!newsItem) {
    return (
      <Layout>
        <div className="container max-w-4xl mx-auto py-8 px-4 flex flex-col items-center justify-center">
          <h2 className="text-2xl font-bold mb-4">뉴스를 찾을 수 없습니다</h2>
          <Button asChild>
            <Link to="/news">뉴스 목록으로 돌아가기</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card className="border-none shadow-sm">
          <CardContent className="p-6">
            <div className="mb-6">
              <Button 
                variant="ghost" 
                size="sm" 
                className="mb-4"
                onClick={() => navigate('/news')}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                목록으로 돌아가기
              </Button>
              
              <h1 className="text-2xl font-bold mb-2">{newsItem?.title}</h1>
              <div className="text-sm text-muted-foreground">
                {formatDate(newsItem?.created_at || newsItem?.createdAt || '')}
              </div>
            </div>
            
            <div className="my-8 border-t border-b py-8">
              <div 
                className="prose prose-sm md:prose-base max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: newsItem?.content || '' }}
              />
            </div>
            
            <div className="flex justify-between items-center text-sm text-muted-foreground mt-4">
              <div>조회 {newsItem?.view_count || newsItem?.viewCount}</div>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => navigate('/news')}
              >
                목록으로
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NewsDetail;
